import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, FormControl, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Tabs, Tab,
  Grid, Card, CardContent, Switch, Divider, IconButton, alpha, Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Assignment, Refresh, SupportAgent, Settings, CheckCircle, Warning,
  ErrorOutline, PriorityHigh, Category, Visibility, Send, Person,
} from '@mui/icons-material';
import { adminAPI, settingsAPI, ticketsAPI } from '../../api/client';
import { integerOnly } from '../../utils/inputHelpers';

const statusColors = { open: 'warning', in_progress: 'info', resolved: 'success', closed: 'default', escalated: 'error', awaiting_response: 'secondary' };
const priorityColors = { low: 'default', medium: 'info', high: 'warning', urgent: 'error' };

function AdminTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [updateDialog, setUpdateDialog] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: '', resolution: '' });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [viewTicket, setViewTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [ts, setTs] = useState({
    auto_assign: 'false', sla_hours: '24', escalation_hours: '48',
    allow_reopen: 'true', require_resolution_notes: 'true',
    max_reopens: '3', notify_on_escalation: 'true',
  });

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await adminAPI.listTickets(params);
      setTickets(res.data.tickets || res.data || []);
      setTotal(res.data.total || res.data.length || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, [page, statusFilter, categoryFilter]);
  useEffect(() => {
    settingsAPI.list({ category: 'ticket' }).then(res => {
      if (Array.isArray(res.data)) {
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setTs(p => ({ ...p, ...m }));
      }
    }).catch(() => {});
  }, []);

  const handleUpdate = async () => {
    if (!updateDialog) return;
    try {
      if (updateForm.status === 'resolved') {
        await adminAPI.resolveTicket(updateDialog, updateForm.resolution || 'Resolved by admin');
      } else if (updateForm.status) {
        await adminAPI.updateTicketStatus(updateDialog, updateForm.status);
      }
      setUpdateDialog(null);
      setUpdateForm({ status: '', resolution: '' });
      setSuccess('Ticket updated'); fetchTickets();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to update ticket'));
    }
  };

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setTs(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  const handleViewTicket = async (ticket) => {
    setViewTicket(ticket);
    try {
      const res = await ticketsAPI.getMessages(ticket.id);
      setTicketMessages(res.data.messages || res.data || []);
    } catch (_) {
      setTicketMessages([]);
    }
  };

  const handleSendMessage = async () => {
    if (!viewTicket || !newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await ticketsAPI.addMessage(viewTicket.id, { message: newMessage.trim() });
      setNewMessage('');
      const res = await ticketsAPI.getMessages(viewTicket.id);
      setTicketMessages(res.data.messages || res.data || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to send message'));
    } finally {
      setSendingMessage(false);
    }
  };

  const openCount = tickets.filter(t => t.status === 'open').length;
  const escalatedCount = tickets.filter(t => t.is_escalated).length;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #ef4444, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Ticket Management
          </Typography>
          <Typography variant="body2" color="text.secondary">Manage support tickets, disputes, and cases</Typography>
        </Box>
        <IconButton onClick={fetchTickets} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="All Tickets" icon={<SupportAgent sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Workflow Settings" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Categories & SLA" icon={<Category sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total', count: total, color: '#6366f1', icon: <Assignment /> },
              { label: 'Open', count: openCount, color: '#f59e0b', icon: <Warning /> },
              { label: 'Resolved', count: tickets.filter(t => t.status === 'resolved').length, color: '#10b981', icon: <CheckCircle /> },
              { label: 'Escalated', count: escalatedCount, color: '#ef4444', icon: <PriorityHigh /> },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                      <Box><Typography variant="body2" color="text.secondary">{item.label}</Typography><Typography variant="h5" fontWeight={800}>{item.count}</Typography></Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All Status</MenuItem>
                  {['open','in_progress','escalated','resolved','closed','awaiting_response'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select value={categoryFilter} displayEmpty onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All Categories</MenuItem>
                  {['rent_dispute','maintenance','payment_issue','landlord_complaint','tenant_complaint','eviction','deposit_return','general'].map(c => <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c.replace('_',' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Ticket #</TableCell><TableCell>Subject</TableCell><TableCell>Category</TableCell>
                <TableCell>Priority</TableCell><TableCell>Status</TableCell><TableCell>Escalated</TableCell>
                <TableCell>Date</TableCell><TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                : tickets.length === 0 ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No tickets found</TableCell></TableRow>
                : tickets.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell><Typography fontWeight={600} variant="body2">{t.ticket_number}</Typography></TableCell>
                    <TableCell><Typography noWrap sx={{ maxWidth: 200 }} variant="body2">{t.subject}</Typography></TableCell>
                    <TableCell><Chip label={t.category?.replace('_',' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Chip label={t.priority} size="small" color={priorityColors[t.priority]||'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Chip label={t.status?.replace('_',' ')} size="small" color={statusColors[t.status]||'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>{t.is_escalated ? <Chip label="Yes" size="small" color="error" /> : <Typography variant="body2">No</Typography>}</TableCell>
                    <TableCell><Typography variant="body2">{new Date(t.created_at).toLocaleDateString()}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => handleViewTicket(t)} color="primary"><Visibility fontSize="small" /></IconButton>
                        <Button size="small" startIcon={<Assignment />} onClick={() => {
                          setUpdateDialog(t.id);
                          setUpdateForm({ status: t.status, resolution: t.resolution || '' });
                        }}>Update</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 15 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><Pagination count={Math.ceil(total/15)} page={page} onChange={(e,v)=>setPage(v)} color="primary" /></Box>}
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Ticket Workflow</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'auto_assign', label: 'Auto-Assign Tickets', desc: 'Route tickets to available agents automatically' },
                  { key: 'allow_reopen', label: 'Allow Ticket Reopen', desc: 'Users can reopen resolved tickets' },
                  { key: 'require_resolution_notes', label: 'Require Resolution Notes', desc: 'Agents must provide notes when resolving' },
                  { key: 'notify_on_escalation', label: 'Notify on Escalation', desc: 'Send alerts when tickets are escalated' },
                ].map(item => (
                  <Stack key={item.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Box><Typography fontWeight={600}>{item.label}</Typography><Typography variant="body2" color="text.secondary">{item.desc}</Typography></Box>
                    <Switch checked={ts[item.key]==='true'} onChange={(e)=>saveSetting(item.key, e.target.checked?'true':'false')} />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>SLA & Escalation</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'sla_hours', label: 'SLA Response Time (hours)' },
                  { key: 'escalation_hours', label: 'Auto-Escalation After (hours)' },
                  { key: 'max_reopens', label: 'Max Ticket Reopens' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <TextField size="small" type="number" value={ts[item.key]}
                      onChange={(e) => setTs(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={integerOnly}
                      onBlur={(e) => saveSetting(item.key, e.target.value)} sx={{ width: 120 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Ticket Categories</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  { name: 'Rent Dispute', color: '#ef4444' }, { name: 'Maintenance', color: '#f59e0b' },
                  { name: 'Payment Issue', color: '#6366f1' }, { name: 'Landlord Complaint', color: '#3b82f6' },
                  { name: 'Tenant Complaint', color: '#8b5cf6' }, { name: 'Eviction', color: '#dc2626' },
                  { name: 'Deposit Return', color: '#10b981' }, { name: 'General', color: '#64748b' },
                ].map((cat, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeftWidth: 4, borderLeftColor: cat.color }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={600}>{cat.name}</Typography>
                      <Switch defaultChecked />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Priority Levels</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { level: 'Urgent', sla: '4 hours', color: '#ef4444', desc: 'Immediate action required' },
                  { level: 'High', sla: '12 hours', color: '#f59e0b', desc: 'Same day response' },
                  { level: 'Medium', sla: '24 hours', color: '#3b82f6', desc: 'Next business day' },
                  { level: 'Low', sla: '48 hours', color: '#64748b', desc: 'Within 2 business days' },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>
                          <ErrorOutline />
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600}>{item.level}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                        </Box>
                      </Stack>
                      <Chip label={`SLA: ${item.sla}`} size="small" variant="outlined" />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* View Ticket Detail Dialog */}
      <Dialog open={!!viewTicket} onClose={() => { setViewTicket(null); setTicketMessages([]); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Ticket Details</DialogTitle>
        <DialogContent>
          {viewTicket && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Header info */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Ticket #</Typography>
                    <Typography fontWeight={700}>{viewTicket.ticket_number}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box><Chip label={viewTicket.status?.replace('_',' ')} size="small" color={statusColors[viewTicket.status]||'default'} sx={{ textTransform: 'capitalize' }} /></Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Priority</Typography>
                    <Box><Chip label={viewTicket.priority} size="small" color={priorityColors[viewTicket.priority]||'default'} sx={{ textTransform: 'capitalize' }} /></Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Typography fontWeight={600} sx={{ textTransform: 'capitalize' }}>{viewTicket.category?.replace('_',' ')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography>{new Date(viewTicket.created_at).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                    <Typography>{viewTicket.updated_at ? new Date(viewTicket.updated_at).toLocaleString() : 'N/A'}</Typography>
                  </Grid>
                  {viewTicket.is_escalated && (
                    <Grid item xs={12}>
                      <Alert severity="error" variant="outlined" sx={{ py: 0 }}>This ticket has been escalated</Alert>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Subject and description */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Subject</Typography>
                <Typography>{viewTicket.subject}</Typography>
              </Box>
              {viewTicket.description && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Description</Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{viewTicket.description}</Typography>
                  </Paper>
                </Box>
              )}

              {/* Resolution */}
              {viewTicket.resolution && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Resolution</Typography>
                  <Alert severity="success" variant="outlined">{viewTicket.resolution}</Alert>
                </Box>
              )}

              <Divider />

              {/* Messages thread */}
              <Typography variant="subtitle2" fontWeight={700}>Messages ({ticketMessages.length})</Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {ticketMessages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No messages yet</Typography>
                ) : ticketMessages.map((msg, i) => (
                  <Paper key={msg.id || i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Avatar sx={{ width: 28, height: 28, bgcolor: msg.is_admin ? 'primary.main' : 'grey.400', fontSize: '0.75rem' }}>
                        <Person sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {msg.sender_name || (msg.is_admin ? 'Admin' : 'User')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{msg.content || msg.message}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Box>

              {/* Reply box */}
              {viewTicket.status !== 'closed' && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth size="small" placeholder="Type a reply..."
                    value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }}}
                  />
                  <Button variant="contained" onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()}
                    startIcon={<Send />}>Send</Button>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewTicket?.status !== 'closed' && viewTicket?.status !== 'resolved' && (
            <Button color="success" variant="contained" onClick={() => {
              setUpdateDialog(viewTicket.id);
              setUpdateForm({ status: 'resolved', resolution: '' });
              setViewTicket(null);
            }}>Resolve Ticket</Button>
          )}
          <Button onClick={() => { setViewTicket(null); setTicketMessages([]); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!updateDialog} onClose={() => setUpdateDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Update Ticket</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <Select value={updateForm.status} onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}>
              {['open','in_progress','awaiting_response','resolved','closed','escalated'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth multiline rows={3} label="Resolution / Notes" value={updateForm.resolution}
            onChange={(e) => setUpdateForm({ ...updateForm, resolution: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminTicketsPage;
